import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiCallError } from '@/lib/api';
import { toast } from '@/components/ui/sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FieldError } from '@/components/ui/field-error';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * El tenant agrega un RUC nuevo desde su panel. Queda pendiente de
 * validación contra SET — el contador lo valida después desde el admin.
 */
export function AppRucAddDialog({ open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    ruc: '',
    razonSocial: '',
    regimen: 'iva_irp' as 'iva_irp' | 'ire_simple',
  });
  const [issues, setIssues] = useState<Record<string, string[]>>({});

  useEffect(() => {
    if (open) {
      setForm({ ruc: '', razonSocial: '', regimen: 'iva_irp' });
      setIssues({});
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: () =>
      api('/api/app/rucs', {
        method: 'POST',
        body: JSON.stringify({
          ruc: form.ruc.trim(),
          razonSocial: form.razonSocial.trim(),
          regimen: form.regimen,
        }),
      }),
    onSuccess: () => {
      toast.success('RUC cargado. Tu contador lo va a validar en breve.');
      qc.invalidateQueries({ queryKey: ['app-me'] });
      onOpenChange(false);
    },
    onError: (err) => {
      if (err instanceof ApiCallError) {
        if (err.status === 409 && err.body.error === 'tenant_already_has_ruc') {
          toast.error('Ya tenés un RUC cargado. Editalo desde el botón "Editar".');
          return;
        }
        if (err.status === 409 && err.body.error === 'ruc_claimed_by_other_tenant') {
          setIssues({
            ruc: [
              'Este RUC ya está registrado y validado en otra cuenta. Si es tuyo, hablá con tu contador.',
            ],
          });
          return;
        }
        if (err.status === 400) {
          const body = err.body as { issues?: Record<string, string[]> };
          if (body.issues) {
            setIssues(body.issues);
            return;
          }
        }
      }
      toast.error('No se pudo guardar el RUC');
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setIssues({});
    mutation.mutate();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={onSubmit}>
          <DialogHeader>
            <DialogTitle>Cargar mi RUC</DialogTitle>
            <DialogDescription>
              Cargá los datos como aparecen en la SET. Tu contador va a validarlo antes de que
              empecemos a procesar facturas. Cada contribuyente tiene un único RUC.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-1.5">
              <Label htmlFor="ruc" required>
                RUC
              </Label>
              <Input
                id="ruc"
                value={form.ruc}
                onChange={(e) => setForm({ ...form, ruc: e.target.value })}
                placeholder="1234567-8"
                autoFocus
              />
              <FieldError error={issues.ruc} />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="razonSocial" required>
                Razón social
              </Label>
              <Input
                id="razonSocial"
                value={form.razonSocial}
                onChange={(e) => setForm({ ...form, razonSocial: e.target.value })}
                placeholder="JUAN PÉREZ"
              />
              <FieldError error={issues.razonSocial} />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="regimen" required>
                Régimen tributario
              </Label>
              <Select
                value={form.regimen}
                onValueChange={(v) => setForm({ ...form, regimen: v as typeof form.regimen })}
              >
                <SelectTrigger id="regimen">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="iva_irp">IVA + IRP</SelectItem>
                  <SelectItem value="ire_simple">IRE Simple</SelectItem>
                </SelectContent>
              </Select>
              <FieldError error={issues.regimen} />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Cargando…' : 'Agregar RUC'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
